/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { findPdfPathByPolicyCode } from './policy-reference.utils'
import {
  BOLD_MD_RE,
  ITALIC_MD_RE,
  STRIKE_MD_RE,
  INLINE_CODE_MD_RE,
  POLICY_ANCHOR_MD_RE,
  ACTION_LINK_MD_RE,
  LINK_MD_RE,
  EMAIL_ANGLE_RE,
  PAREN_POLICY_CODE_RE,
  PAREN_LABELED_POLICY_CODE_RE,
  INLINE_POLICY_CODE_RE,
  POLICY_LINK_HTML_RE,
  HORIZONTAL_RULE_RE,
  TABLE_SEPARATOR_ROW_RE,
  ORDERED_LIST_START_RE,
  findNearestPageRefBefore,
} from './regex-matchers'
import { renderActionLink } from './action-link-parser'

// Note: window-based copy/export handlers were removed. Actions are delegated to the bubble handler.

export function parseMarkdownToHtml(
  text: string,
  selectedPolicyContents?: any[],
  allPolicies?: any[],
): string {
  if (!text) return ''

  const lines = text.split('\n')
  const processedLines: string[] = []
  let inTable = false
  let tableRows: string[] = []
  let isFirstRow = false
  let currentTableColCount = 0

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Handle horizontal rules
    if (line.match(HORIZONTAL_RULE_RE)) {
      processedLines.push('<hr>')
      continue
    }

    // Handle headers with different levels
    if (line.startsWith('### ')) {
      processedLines.push(
        `<h3>${processInlineMarkdown(line.substring(4), selectedPolicyContents, allPolicies)}</h3>`,
      )
      continue
    } else if (line.startsWith('## ')) {
      processedLines.push(
        `<h2>${processInlineMarkdown(line.substring(3), selectedPolicyContents, allPolicies)}</h2>`,
      )
      continue
    } else if (line.startsWith('# ')) {
      processedLines.push(
        `<h1>${processInlineMarkdown(line.substring(2), selectedPolicyContents, allPolicies)}</h1>`,
      )
      continue
    }

    // Handle tables
    if (line.includes('|')) {
      if (!inTable) {
        inTable = true
        tableRows = []
        isFirstRow = true
      }

      // Skip separator rows (rows with only |, -, and spaces)
      if (!line.match(TABLE_SEPARATOR_ROW_RE)) {
        const cells = line
          .split('|')
          .map((cell) => cell.trim())
          .filter((cell) => cell)

        // Use th for first row (header), td for subsequent rows
        const cellTag = isFirstRow ? 'th' : 'td'
        if (isFirstRow) {
          currentTableColCount = Math.max(1, cells.length)
        }
        const rowHtml = cells
          .map(
            (cell) =>
              `<${cellTag}>${processInlineMarkdown(
                cell,

                selectedPolicyContents,
                allPolicies,
              )}</${cellTag}>`,
          )
          .join('')
        tableRows.push(`<tr>${rowHtml}</tr>`)
        isFirstRow = false
      }
      continue
    } else if (inTable) {
      // End of table
      inTable = false
      if (tableRows.length > 0) {
        const tableId = `table-${Math.random().toString(36).substr(2, 9)}`
        const copyButton = `
          <button class="table-action-btn" data-action="copy-table" data-table-id="${tableId}" title="Copy table">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path
                d='M10.5 2.0028C9.82495 2.01194 9.4197 2.05103 9.09202 2.21799C8.71569 2.40973 8.40973 2.71569 8.21799 3.09202C8.05103 3.4197 8.01194 3.82495 8.0028 4.5M19.5 2.0028C20.1751 2.01194 20.5803 2.05103 20.908 2.21799C21.2843 2.40973 21.5903 2.71569 21.782 3.09202C21.949 3.4197 21.9881 3.82494 21.9972 4.49999M21.9972 13.5C21.9881 14.175 21.949 14.5803 21.782 14.908C21.5903 15.2843 21.2843 15.5903 20.908 15.782C20.5803 15.949 20.1751 15.9881 19.5 15.9972M22 7.99999V9.99999M14.0001 2H16M5.2 22H12.8C13.9201 22 14.4802 22 14.908 21.782C15.2843 21.5903 15.5903 21.2843 15.782 20.908C16 20.4802 16 19.9201 16 18.8V11.2C16 10.0799 16 9.51984 15.782 9.09202C15.5903 8.71569 15.2843 8.40973 14.908 8.21799C14.4802 8 13.9201 8 12.8 8H5.2C4.0799 8 3.51984 8 3.09202 8.21799C2.71569 8.40973 2.40973 21.5903 3.09202 21.782C3.51984 22 4.07989 22 5.2 22Z'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />         
              </svg>
            Copy
          </button>
        `
        const exportButton = `
          <button class="table-action-btn" data-action="export-csv" data-table-id="${tableId}" title="Export as CSV">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export CSV
          </button>
        `
        processedLines.push(`
          <div class="table-container">            
            <table id="${tableId}" style="--ai-cols: ${Math.max(
          1,
          currentTableColCount,
        )}">${tableRows.join('')}</table>
            <div class="table-actions">
              ${copyButton}
              ${exportButton}
            </div>
          </div>
        `)
      }
      tableRows = []
      currentTableColCount = 0
    }

    // Handle simple lists (no nesting)
    if (line.startsWith('- ')) {
      const listContent = processInlineMarkdown(
        line.substring(2),
        selectedPolicyContents,
        allPolicies,
      )
      processedLines.push(`<li>${listContent}</li>`)
      continue
    }

    // Handle numbered lists
    if (line.match(ORDERED_LIST_START_RE)) {
      const listContent = processInlineMarkdown(
        line.replace(/^\d+\.\s/, ''),
        selectedPolicyContents,
        allPolicies,
      )
      processedLines.push(`<li>${listContent}</li>`)
      continue
    }

    // Handle empty lines
    if (line === '') {
      // processedLines.push('<br>')
      continue
    }

    // Regular text line
    const formattedLine = processInlineMarkdown(line, selectedPolicyContents, allPolicies)
    processedLines.push(`<p>${formattedLine}</p>`)
  }

  // Handle table if still open at end
  if (inTable && tableRows.length > 0) {
    processedLines.push(`<table>${tableRows.join('')}</table>`)
  }

  // Wrap lists in appropriate tags
  let result = processedLines.join('')

  // Simple list wrapping - find consecutive <li> tags and wrap them
  result = result.replace(/(<li>.*?<\/li>)+/g, (match) => {
    return `<ul>${match}</ul>`
  })

  return result
}

function processInlineMarkdown(
  text: string,
  selectedPolicyContents?: any[],
  allPolicies?: any[],
): string {
  let processedText = text

  // Process the text multiple times to catch all references
  let previousText = ''
  let iterations = 0
  const maxIterations = 3 // Prevent infinite loops

  while (processedText !== previousText && iterations < maxIterations) {
    previousText = processedText
    iterations += 1

    processedText = processedText
      // Bold text
      .replace(BOLD_MD_RE, '<strong>$1</strong>')
      // Italic text
      .replace(ITALIC_MD_RE, '<em>$1</em>')
      // Strikethrough
      .replace(STRIKE_MD_RE, '<del>$1</del>')
      // Inline code
      .replace(INLINE_CODE_MD_RE, '<code>$1</code>')
      // Policy page references with anchor format (e.g., "Reference: [page 18](#POL1327DIR_0821)") - Process this FIRST
      .replace(POLICY_ANCHOR_MD_RE, (match, pageRef, linkReference) => {
        // Skip if already contains HTML links
        if (match.includes('class="policy-page-link"')) {
          return match
        }
        // Find PDF path by policy code
        const pdfPath = findPdfPathByPolicyCode(linkReference, allPolicies, selectedPolicyContents)
        if (pdfPath) {
          const linkHtml = `<a href="#" class="policy-page-link" 
          data-text="${pageRef}" 
          data-pdf-path="${pdfPath}" 
          data-policy-code="${linkReference}" 
          title="Open Policy PDF - ${linkReference}"
          target="_blank" rel="noopener noreferrer">${pageRef}</a>`
          return linkHtml
        }
        // Fallback: Convert to normal link if policy code not found
        return `<a href="#${linkReference}" target="_blank" rel="noopener noreferrer">${pageRef}</a>`
      })
      // Inline action handlers such as #action:policy_wording_link?policy_code=CODE
      .replace(ACTION_LINK_MD_RE, (fullMatch, linkText: string, actionPayload: string) => {
        return renderActionLink({
          text: linkText,
          actionPayload,
          selectedPolicyContents,
          allPolicies,
        })
      })
      // Links
      .replace(LINK_MD_RE, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
      // Email links
      .replace(EMAIL_ANGLE_RE, '<a href="mailto:$1">$1</a>')
  }

  try {
    // Convert plain policy code mentions like (DP2122-66_0721) or (BudgetDirect-CarLowKM_0321-SPDS_0722)
    // into clickable policy links. Avoid short tokens like (PDS) by requiring length >= 4.
    processedText = processedText.replace(
      PAREN_POLICY_CODE_RE,
      (m, code, offset: number, str: string) => {
        try {
          const pdfPath = findPdfPathByPolicyCode(code, allPolicies, selectedPolicyContents)
          if (!pdfPath) return m

          // Attempt to capture a nearby page reference appearing before the code, e.g. "Part A page 24" or "page 24"
          const dataText = findNearestPageRefBefore(str, offset)

          return `<a href="#" class="policy-page-link" data-text="${dataText}" data-pdf-path="${pdfPath}" data-policy-code="${code}" title="Open Policy PDF - ${code}">${m}</a>`
        } catch {
          return m
        }
      },
    )

    // Convert "(Policy Code: CODE)" inside parentheses
    processedText = processedText.replace(
      PAREN_LABELED_POLICY_CODE_RE,
      (m, code, offset: number, str: string) => {
        try {
          const pdfPath = findPdfPathByPolicyCode(code, allPolicies, selectedPolicyContents)
          if (!pdfPath) return m
          const dataText = findNearestPageRefBefore(str, offset)
          return `<a href="#" class="policy-page-link" data-text="${dataText}" data-pdf-path="${pdfPath}" data-policy-code="${code}" title="Open Policy PDF - ${code}">${m}</a>`
        } catch {
          return m
        }
      },
    )

    // Convert inline "Policy Code: CODE" not in parentheses, preserving preceding whitespace or start
    processedText = processedText.replace(
      INLINE_POLICY_CODE_RE,
      (full, leading: string, code: string, offset: number, str: string) => {
        try {
          const pdfPath = findPdfPathByPolicyCode(code, allPolicies, selectedPolicyContents)
          if (!pdfPath) return full
          const dataText = findNearestPageRefBefore(str, offset)
          const visible = `Policy Code: ${code}`
          const anchor = `<a href="#" class="policy-page-link" data-text="${dataText}" data-pdf-path="${pdfPath}" data-policy-code="${code}" title="Open Policy PDF - ${code}">${visible}</a>`
          return `${leading}${anchor}`
        } catch {
          return full
        }
      },
    )

    // If this line references multiple policies via policy-page-link anchors, append a load-comparison action
    const policyCodeRegex = POLICY_LINK_HTML_RE
    const codes: string[] = []
    let match: RegExpExecArray | null
    while ((match = policyCodeRegex.exec(processedText)) !== null) {
      const code = match[1]
      if (code && !codes.includes(code)) codes.push(code)
    }
    if (codes.length >= 2) {
      const policyCodesAttr = codes.join(',')
      processedText = `${processedText} <a href="#" class="load-comparison-link" data-policy-codes="${policyCodesAttr}" title="Compare these policies">Compare</a>`
    }
  } catch {
    // no-op if regex fails
  }

  return processedText
}
